import { useCallback, useEffect, useRef, useState } from "react";

export interface GeoPosition {
  lat: number;
  lon: number;
  /** Yatay doğruluk (metre). */
  accuracy: number;
  timestamp: number;
}

export type GeoStatus = "idle" | "prompt" | "locating" | "ready" | "denied" | "error";

export interface GeolocationState {
  position: GeoPosition | null;
  status: GeoStatus;
  message: string | null;
  /** Konum izni isteğini başlatır (kullanıcı hareketiyle çağrılmalıdır). */
  request: () => void;
}

const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 10_000,
  timeout: 20_000,
};

export function useGeolocation(enabled: boolean): GeolocationState {
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  const request = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStatus("error");
      setMessage("Bu tarayıcı konum servisini desteklemiyor.");
      return;
    }

    setStatus((current) => (current === "ready" ? current : "locating"));

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
        setStatus("ready");
        setMessage(null);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setStatus("denied");
          setMessage(
            "Konum izni verilmedi. Tarayıcı ayarlarından bu siteye konum izni verin ya da konumunuzu elle girin.",
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setStatus("error");
          setMessage("Konum alınamadı. Açık alana çıkıp yeniden deneyin.");
        } else {
          setStatus("error");
          setMessage("Konum isteği zaman aşımına uğradı. Yeniden deneyin.");
        }
      },
      OPTIONS,
    );

    watchIdRef.current = id;
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, attempt]);

  return { position, status, message, request };
}
